<!--
    אישי - the form of a personal item on the calendar (מילואים, רופא, חופשה...): a block of time that
    is not a meeting, a task or an event, so it needs no customer. Mounted once in App.vue; opened with
    $root.$emit('openPersonalItemModal', item | { id } | null, date, 'HH:MM') from the calendar's day
    popup (a new one on that day / hour), from a pill of the calendar (an existing one, fetched by id)
    and from the side menu's +.

    Fields: what (with quick picks), the first day, the last day (a multi-day item), all day or from /
    to, a repeat rule (כל יום / כל שבוע בימים... / כל חודש, until a day - the api makes one row per
    occurrence, a series), whose (the account's users - shown when there is more than one), notes, and
    - for an account with online booking - whether the time is blocked for it. A row of a series is
    saved for itself alone or for itself and those after it (applyTo), and deleted the same two ways.
    Saved through POST personal-items; the calendar's cache takes the saved row
    (CalendarHandler.addItemToCache) or drops the deleted one; a series change reloads the calendars.
-->
<template>
    <Modal ref="modal"
           :title="modalTitle"
           :hideFooter="true"
           class="personalItemModal">

        <template v-slot:body>

            <div class="personalItemForm"
                 data-personal-item-form>

                <!-- a row of a series: what it repeats, and whether the save is for this row alone -->
                <div class="personalItemSeries"
                     v-if="item.id && item.seriesId"
                     data-personal-series>
                    <div class="personalItemSeriesTitle">
                        <i class="fa-solid fa-rotate"></i>
                        פריט חוזר: {{ item.recurrenceText || 'סדרה' }}
                        <span class="personalItemMuted"
                              v-if="seriesCount">({{ seriesCount }} פריטים)</span>
                    </div>
                    <div class="personalItemSeriesChoice">
                        <label class="personalItemCheck"
                               @click="applyTo = 'one'">
                            <span class="planup-checkbox planup-checkbox-purple"
                                  :class="{ checked: applyTo == 'one' }"><i class="fa fa-check"></i></span>
                            <span>השינוי חל רק על הפריט הזה</span>
                        </label>
                        <label class="personalItemCheck"
                               data-personal-apply-series
                               @click="applyTo = 'series'">
                            <span class="planup-checkbox planup-checkbox-purple"
                                  :class="{ checked: applyTo == 'series' }"><i class="fa fa-check"></i></span>
                            <span>על הפריט הזה ועל הבאים אחריו</span>
                        </label>
                    </div>
                </div>

                <div class="row">

                    <div class="col-12 mb-3">
                        <label class="form-label">מה</label>
                        <input class="form-control"
                               v-model="item.title"
                               ref="titleInput"
                               maxlength="255"
                               placeholder="מילואים, רופא, חופשה..."
                               data-personal-title
                               @keyup.enter="save">
                        <div class="personalItemChips">
                            <span v-for="suggestion in suggestions"
                                  :key="suggestion"
                                  class="personalItemChip"
                                  :class="{ active: item.title == suggestion }"
                                  @click="item.title = suggestion">{{ suggestion }}</span>
                        </div>
                    </div>

                    <div class="col-6 mb-3">
                        <label class="form-label">מתאריך</label>
                        <DatePicker v-model="dayDmy"
                                    :enableTime="false"
                                    class="datePickerAsInput"
                                    style="width:100%"></DatePicker>
                    </div>

                    <div class="col-6 mb-3">
                        <label class="form-label">
                            עד תאריך
                            <small class="personalItemMuted">(כמה ימים)</small>
                        </label>
                        <DatePicker v-model="endDayDmy"
                                    :enableTime="false"
                                    class="datePickerAsInput"
                                    style="width:100%"></DatePicker>
                        <span v-if="endDayDmy"
                              class="personalItemLink"
                              @click="endDayDmy = ''">יום אחד בלבד</span>
                    </div>

                    <div class="col-12 mb-2">
                        <label class="personalItemCheck"
                               @click="toggleAllDay">
                            <span class="planup-checkbox planup-checkbox-purple"
                                  :class="{ checked: allDay }"><i class="fa fa-check"></i></span>
                            <span>כל היום</span>
                        </label>
                    </div>

                    <div class="col-6 mb-3"
                         v-if="!allDay">
                        <label class="form-label">משעה</label>
                        <div>
                            <WheelTimePicker v-model="item.time"
                                             :clearable="false"></WheelTimePicker>
                        </div>
                    </div>

                    <div class="col-6 mb-3"
                         v-if="!allDay">
                        <label class="form-label">עד שעה</label>
                        <div>
                            <WheelTimePicker v-model="item.endTime"></WheelTimePicker>
                        </div>
                    </div>

                    <!-- the repeat rule: a new item, or a series row saved for itself and those after it -->
                    <div class="col-12 mb-2"
                         v-if="showRepeat">
                        <label class="personalItemCheck"
                               data-personal-repeat
                               @click="toggleRepeat">
                            <span class="planup-checkbox planup-checkbox-purple"
                                  :class="{ checked: repeat.on }"><i class="fa fa-check"></i></span>
                            <span>חוזר</span>
                            <span class="personalItemMuted"
                                  v-if="!repeat.on">(כל יום, כל שבוע, כל חודש)</span>
                        </label>
                    </div>

                    <div class="col-12 mb-3 personalItemRepeat"
                         v-if="showRepeat && repeat.on"
                         data-personal-repeat-box>

                        <SegmentedChoice v-model="repeat.freq"
                                         :options="freqOptions"></SegmentedChoice>

                        <div class="personalItemRepeatRow"
                             v-if="repeat.freq != 'monthly'">
                            <span>כל</span>
                            <input type="number"
                                   class="form-control personalItemInterval"
                                   min="1"
                                   max="30"
                                   v-model.number="repeat.interval"
                                   data-personal-interval>
                            <span>{{ intervalUnit }}</span>
                        </div>

                        <div class="personalItemWeekdays"
                             v-if="repeat.freq == 'weekly'"
                             data-personal-weekdays>
                            <span v-for="(name, index) in weekdayNames"
                                  :key="index"
                                  class="dayOfWeekToggle"
                                  :class="{ chosen: repeat.days.indexOf(index) > -1 }"
                                  :data-weekday="index"
                                  @click="toggleWeekday(index)">{{ name }}</span>
                        </div>

                        <div class="personalItemRepeatRow">
                            <span>עד תאריך</span>
                            <DatePicker v-model="repeat.untilDmy"
                                        :enableTime="false"
                                        class="datePickerAsInput personalItemUntil"></DatePicker>
                            <span class="personalItemMuted">(עד שנה)</span>
                        </div>

                        <div class="personalItemMuted">{{ repeatSummary }}</div>
                    </div>

                    <div class="col-12 mb-3"
                         v-if="users.length > 1">
                        <label class="form-label">של</label>
                        <Multiselect v-model="owner"
                                     :options="users"
                                     label="name"
                                     track-by="id"
                                     :allow-empty="false"
                                     placeholder=""
                                     selectLabel=""
                                     selectedLabel=""
                                     deselect-label="">
                            <template slot="noResult">לא נמצאו תוצאות</template>
                            <template slot="noOptions">אין אפשרויות</template>
                        </Multiselect>
                    </div>

                    <div class="col-12 mb-3">
                        <label class="form-label">הערות</label>
                        <textarea class="form-control"
                                  v-model="item.notes"
                                  rows="3"
                                  maxlength="4000"
                                  style="resize:none"></textarea>
                    </div>

                    <div class="col-12 mb-2"
                         v-if="bookingFeatureEnabled">
                        <label class="personalItemCheck"
                               @click="item.blocksBooking = !item.blocksBooking">
                            <span class="planup-checkbox planup-checkbox-purple"
                                  :class="{ checked: item.blocksBooking }"><i class="fa fa-check"></i></span>
                            <span>הזמן חסום לתיאום פגישות אונליין</span>
                        </label>
                        <div class="personalItemMuted"
                             style="margin-right:34px">לקוחות לא יוכלו לקבוע פגישה בזמן הזה דרך קישור התיאום.</div>
                    </div>

                </div>

                <div class="personalItemButtons">
                    <span class="personalItemDeletes"
                          v-if="item.id">
                        <span class="personalItemDelete"
                              data-personal-delete
                              @click="remove('one')">
                            <i class="fa-regular fa-trash-can"></i>
                            מחיקה
                        </span>
                        <span class="personalItemDelete"
                              v-if="item.seriesId"
                              data-personal-delete-following
                              @click="remove('following')">
                            <i class="fa-solid fa-rotate"></i>
                            מחיקה מכאן והלאה
                        </span>
                    </span>
                    <button class="btn btn-primary"
                            :disabled="saving"
                            data-personal-save
                            @click="save">
                        {{ saving ? 'שומר...' : 'שמירה' }}
                    </button>
                </div>

            </div>

        </template>
    </Modal>
</template>
<script>
import Modal from '../Common/Modal.vue';
import DatePicker from '../Common/DatePicker';
import WheelTimePicker from '../Common/WheelTimePicker.vue';
import SegmentedChoice from '../Common/SegmentedChoice.vue';
import Multiselect from 'vue-multiselect';
import axiosV2 from '../../core/http/axios_api_v2';

const SUGGESTIONS = ['מילואים', 'רופא', 'חופשה', 'אישי'];
const WEEKDAY_NAMES = ["א'", "ב'", "ג'", "ד'", "ה'", "ו'", "ש'"];
const FREQ_OPTIONS = [
    { id: 'daily', name: 'כל יום' },
    { id: 'weekly', name: 'כל שבוע' },
    { id: 'monthly', name: 'כל חודש' },
];

// 'YYYY-MM-DD' ↔ 'DD/MM/YYYY' (Common/DatePicker speaks d/m/Y)
function ymdToDmy(ymd) {
    if (!ymd || typeof ymd != 'string')
        return '';
    const parts = ymd.substring(0, 10).split('-');
    return parts.length == 3 ? parts[2] + '/' + parts[1] + '/' + parts[0] : '';
}

function dmyToYmd(dmy) {
    if (!dmy || typeof dmy != 'string')
        return null;
    const match = dmy.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (!match)
        return null;
    return match[3] + '-' + (match[2].length < 2 ? '0' : '') + match[2] + '-' + (match[1].length < 2 ? '0' : '') + match[1];
}

// 'YYYY-MM-DD' of a local Date
function ymdOfDate(date) {
    const month = date.getMonth() + 1, day = date.getDate();
    return date.getFullYear() + '-' + (month < 10 ? '0' : '') + month + '-' + (day < 10 ? '0' : '') + day;
}

// a local Date of a 'YYYY-MM-DD'
function dateOfYmd(ymd) {
    const parts = String(ymd || '').split('-').map(Number);
    return parts.length == 3 ? new Date(parts[0], parts[1] - 1, parts[2]) : new Date();
}

function emptyItem(date, time) {
    const day = date ? new Date(date) : new Date();
    return {
        id: null,
        title: '',
        date: ymdOfDate(isNaN(day.getTime()) ? new Date() : day),
        endDate: null,
        time: time || null,
        endTime: null,
        notes: '',
        blocksBooking: true,
        userId: null,
        seriesId: null,
        recurrence: null,
        recurrenceText: '',
    };
}

function emptyRepeat() {
    return { on: false, freq: 'weekly', interval: 1, days: [], untilDmy: '' };
}

export default {
    name: 'PersonalItemModal',
    data() {
        return {
            item: emptyItem(),
            dayDmy: '',
            endDayDmy: '',
            allDay: true,
            users: [],
            owner: null,
            saving: false,
            suggestions: SUGGESTIONS,
            weekdayNames: WEEKDAY_NAMES,
            freqOptions: FREQ_OPTIONS,
            // the repeat rule as the form holds it
            repeat: emptyRepeat(),
            // a row of a series: 'one' = this row alone, 'series' = this row and those after it
            applyTo: 'one',
            seriesCount: 0,
        };
    },
    computed: {

        modalTitle() {
            return this.item.id ? 'אישי: ' + (this.item.title || 'אישי') : 'פריט אישי חדש';
        },

        // Online booking is a per-account service the system admin turns on (biz-services id 10)
        bookingFeatureEnabled() {
            const user = this.$store.getters.getUser || {};
            return (user.bizServices || []).map(String).indexOf('10') > -1;
        },

        me() {
            return this.$store.state.user || {};
        },

        // the rule is edited on a new item, a single item, or a series row saved for the rows after it
        showRepeat() {
            return !this.item.seriesId || this.applyTo == 'series';
        },

        intervalUnit() {
            if (this.repeat.freq == 'daily')
                return this.repeat.interval > 1 ? 'ימים' : 'יום';
            return this.repeat.interval > 1 ? 'שבועות' : 'שבוע';
        },

        // כל שבוע בימי א', ג', עד 31/12/2026 - what the api will say about it
        repeatSummary() {
            const r = this.repeat;
            const n = r.interval > 1 ? r.interval : 1;
            let text = '';
            if (r.freq == 'daily')
                text = n > 1 ? 'כל ' + n + ' ימים' : 'כל יום';
            else if (r.freq == 'weekly') {
                const days = (r.days.length ? r.days : [dateOfYmd(dmyToYmd(this.dayDmy) || this.item.date).getDay()]).slice().sort();
                text = (n > 1 ? 'כל ' + n + ' שבועות' : 'כל שבוע') + ' בימי ' + days.map(d => WEEKDAY_NAMES[d]).join(', ');
            } else
                text = 'כל חודש';
            return text + (r.untilDmy ? ', עד ' + r.untilDmy : '');
        },
    },
    methods: {

        // (item | { id } | null, date, 'HH:MM'): an existing item (fetched when only its id is known),
        // or a new one on that day, at that hour
        show(item, date, time) {

            if (item && item.id && !item.date) {
                axiosV2.get('personal-items/' + item.id).then(response => this.open(response.data)).catch(() => {
                    this.$swal('הפריט לא נמצא');
                });
                return;
            }

            this.open(item && item.id ? item : emptyItem(date, time));
        },

        open(item) {

            this.item = Object.assign(emptyItem(), item);
            this.item.blocksBooking = this.item.blocksBooking !== false;
            this.dayDmy = ymdToDmy(this.item.date);
            this.endDayDmy = ymdToDmy(this.item.endDate);
            this.allDay = !this.item.time;
            this.saving = false;
            this.applyTo = 'one';
            this.seriesCount = item && item.seriesCount ? item.seriesCount : 0;
            this.repeat = this.repeatOf(this.item.recurrence);

            this.loadUsers();
            this.$refs.modal.show();

            setTimeout(() => {
                if (!this.item.id && this.$refs.titleInput)
                    this.$refs.titleInput.focus();
            }, 500);
        },

        // the form's rule out of the api's ({ freq, interval, days, until })
        repeatOf(rule) {
            const repeat = emptyRepeat();
            if (!rule || !rule.freq)
                return repeat;
            repeat.on = true;
            repeat.freq = rule.freq;
            repeat.interval = rule.interval > 1 ? rule.interval : 1;
            repeat.days = Array.isArray(rule.days) ? rule.days.slice() : [];
            repeat.untilDmy = ymdToDmy(rule.until);
            return repeat;
        },

        // the account's users (the task modal's assignees list): whose the item is
        loadUsers() {

            const apply = users => {
                this.users = Array.isArray(users) ? users : [];
                const wanted = this.item.userId || this.me.id;
                this.owner = this.users.find(user => user.id == wanted) || this.users.find(user => user.id == this.me.id) || null;
            };

            apply(this.$store.state.taskAssignees);
            this.$handlers.tasksHandler.loadAssignees().then(apply).catch(() => { });
        },

        toggleAllDay() {
            this.allDay = !this.allDay;
            if (this.allDay) {
                this.item.time = null;
                this.item.endTime = null;
            } else if (!this.item.time) {
                this.item.time = '09:00';
            }
        },

        // on: a weekly rule on the first day's weekday, for three months
        toggleRepeat() {
            this.repeat.on = !this.repeat.on;
            if (!this.repeat.on)
                return;
            const start = dateOfYmd(dmyToYmd(this.dayDmy) || this.item.date);
            if (!this.repeat.days.length)
                this.repeat.days = [start.getDay()];
            if (!this.repeat.untilDmy) {
                const until = new Date(start.getFullYear(), start.getMonth() + 3, start.getDate());
                this.repeat.untilDmy = ymdToDmy(ymdOfDate(until));
            }
        },

        toggleWeekday(index) {
            const at = this.repeat.days.indexOf(index);
            if (at > -1)
                this.repeat.days.splice(at, 1);
            else
                this.repeat.days.push(index);
        },

        // the rule for the api, or null; a string = what is wrong with it
        ruleForApi() {
            if (!this.showRepeat || !this.repeat.on)
                return null;
            const until = dmyToYmd(this.repeat.untilDmy);
            if (!until)
                return 'יש לבחור עד מתי הפריט חוזר';
            let interval = parseInt(this.repeat.interval, 10);
            if (!interval || interval < 1)
                interval = 1;
            return { freq: this.repeat.freq, interval: interval, days: this.repeat.freq == 'weekly' ? this.repeat.days.slice().sort() : [], until: until };
        },

        async save() {

            const date = dmyToYmd(this.dayDmy);
            if (!date) {
                this.$swal('יש לבחור תאריך');
                return;
            }

            let endDate = dmyToYmd(this.endDayDmy);
            if (endDate && endDate <= date)
                endDate = null;

            if (!this.allDay && !this.item.time) {
                this.$swal('יש לבחור שעת התחלה, או לסמן "כל היום"');
                return;
            }

            if (!this.allDay && this.item.endTime && this.item.endTime <= this.item.time) {
                this.$swal('שעת הסיום צריכה להיות אחרי שעת ההתחלה');
                return;
            }

            const rule = this.ruleForApi();
            if (typeof rule == 'string') {
                this.$swal(rule);
                return;
            }

            const body = {
                id: this.item.id || undefined,
                title: (this.item.title || '').trim(),
                date: date,
                endDate: endDate,
                time: this.allDay ? null : this.item.time,
                endTime: this.allDay ? null : (this.item.endTime || null),
                notes: this.item.notes || '',
                blocksBooking: !!this.item.blocksBooking,
                userId: this.owner ? this.owner.id : (this.item.userId || null),
                recurrence: rule,
                applyTo: this.item.seriesId ? this.applyTo : 'one',
            };

            // a series changes many rows: the calendars load the month again
            const touchesSeries = !!rule || (this.item.seriesId && this.applyTo == 'series');

            this.saving = true;

            try {
                const response = await axiosV2.post('personal-items', body);
                const saved = response.data;
                this.saving = false;

                if (!saved || !saved.id) {
                    const message = response.data && response.data.message;
                    this.$swal(typeof message == 'string' ? message : 'שגיאה בשמירה');
                    return;
                }

                if (touchesSeries || saved.endDate) {
                    this.$handlers.calendarHandler.hardResetAllCalendars();
                } else {
                    // an edit may have moved it to another month: out of the old place, into the new one
                    if (this.item.id)
                        this.$handlers.calendarHandler.removeItemFromCache('personal_' + saved.id);
                    this.$handlers.calendarHandler.addItemToCache(saved.date, 'personal', saved);
                }

                this.$root.$emit('personalItemSaved', saved);
                this.$refs.modal.hide();

            } catch (e) {
                this.saving = false;
                // the api's Hebrew sentence (a 400 - axios_api_v2's interceptor hands it over as { message })
                const message = e && (e.message || (e.response && e.response.data && e.response.data.message));
                this.$swal(typeof message == 'string' && /[֐-׿]/.test(message) ? message : 'שגיאה בשמירה');
            }
        },

        // 'one' = this row; 'following' = this row and those after it in its series
        remove(scope) {

            const id = this.item.id;
            if (!id)
                return;

            const following = scope == 'following' && this.item.seriesId;

            this.$swal({
                title: following ? 'למחוק את הפריט הזה ואת כל הבאים אחריו?' : 'למחוק את הפריט מהיומן?',
                text: this.item.title || '',
                confirmButtonText: 'כן, למחוק',
                cancelButtonText: 'לא',
                confirmButtonColor: '#ab8ce4',
                showCancelButton: true,
            }).then(() => {
                axiosV2.delete('personal-items/' + id + (following ? '?scope=following' : '')).then(() => {
                    if (following)
                        this.$handlers.calendarHandler.hardResetAllCalendars();
                    else
                        this.$handlers.calendarHandler.removeItemFromCache('personal_' + id);
                    this.$root.$emit('personalItemDeleted', id);
                    this.$refs.modal.hide();
                }).catch(() => {
                    this.$swal('שגיאה במחיקה');
                });
            }).catch(() => { });
        },
    },
    components: { Modal, DatePicker, WheelTimePicker, SegmentedChoice, Multiselect }
}
</script>
<style>
.personalItemForm {
    direction: rtl;
}

.personalItemChips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.personalItemChip {
    padding: 3px 11px;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: #475569;
    font-size: 12.5px;
    cursor: pointer;
    user-select: none;
    transition: background-color .12s, color .12s, border-color .12s;
}

.personalItemChip:hover {
    background: #f1f5f9;
}

.personalItemChip.active {
    background: rgba(120, 113, 108, .15);
    border-color: rgba(120, 113, 108, .4);
    color: #44403c;
    font-weight: 600;
}

.personalItemCheck {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    cursor: pointer;
    user-select: none;
    color: #334155;
}

.personalItemMuted {
    color: #94a3b8;
    font-size: 12px;
    font-weight: 400;
}

.personalItemLink {
    display: inline-block;
    margin-top: 4px;
    color: #ab8ce4;
    font-size: 12.5px;
    cursor: pointer;
}

/* a row of a series: the rule and the reach of the save */
.personalItemSeries {
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(120, 113, 108, .08);
    border: 1px solid rgba(120, 113, 108, .2);
}

.personalItemSeriesTitle {
    font-weight: 600;
    color: #44403c;
    margin-bottom: 6px;
}

.personalItemSeriesTitle i {
    margin-left: 4px;
    color: #78716c;
}

.personalItemSeriesChoice {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
}

/* the repeat rule */
.personalItemRepeat {
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.personalItemRepeatRow {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    color: #334155;
}

.personalItemInterval {
    width: 70px !important;
    display: inline-block;
    text-align: center;
}

.personalItemUntil {
    width: 150px;
}

.personalItemWeekdays {
    display: flex;
    gap: 6px;
    margin-top: 10px;
}

.personalItemButtons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 10px;
}

.personalItemButtons .btn {
    margin-right: auto;
}

.personalItemDeletes {
    display: flex;
    gap: 16px;
}

.personalItemDelete {
    color: #999;
    cursor: pointer;
    font-size: 13.5px;
}

.personalItemDelete:hover {
    color: #64748b;
}

.personalItemModal .form-control {
    width: 100%;
}
</style>
